import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XeroInvoice } from '../../../types/xero';
import { CALENDAR_ITEM_COLORS } from '../../../types/calendar';
import { ADELAIDE_TZ, isDateOverdue } from '../../../utils/dateUtils';

/** Bills (ACCPAY) are money OUT and get their own presentation — never the invoice styling. */
type InvoiceQuickViewType = 'invoice_due' | 'invoice_created' | 'bill_due';

const TYPE_LABELS: Record<InvoiceQuickViewType, string> = {
  invoice_due: 'Invoice Due',
  invoice_created: 'Invoice Created',
  bill_due: 'Bill Due',
};

/**
 * Tint a calendar colour token to a given alpha.
 * The invoice tokens are 6-digit hex, so the old `${color}20` suffix trick worked for them —
 * but bill_due's token is an rgba() string, where that suffix yields invalid CSS the browser
 * silently drops (chips render with no background or border). Parse both forms instead.
 */
const withAlpha = (color: string, alpha: number): string => {
  const hex = color.match(/^#([0-9a-f]{6})$/i);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  return color;
};

interface InvoiceQuickViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: XeroInvoice | null;
  viewType?: InvoiceQuickViewType;
}

const InvoiceQuickViewModal: React.FC<InvoiceQuickViewModalProps> = ({
  isOpen,
  onClose,
  invoice,
  viewType = 'invoice_due',
}) => {
  // Hook must run before the early return, or the modal opening/closing changes hook order.
  const navigate = useNavigate();

  if (!isOpen || !invoice) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: invoice.CurrencyCode || 'AUD',
    }).format(amount);
  };

  const parseXeroDate = (dateStr: string | undefined): string => {
    if (!dateStr) return 'Not set';
    const match = dateStr.match(/Date\((\d+)(?:[+-]\d+)?\)/);
    if (match) {
      const date = new Date(Number(match[1]));
      return date.toLocaleDateString('en-AU', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Not set';
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Reduce a Xero due date to the YYYY-MM-DD calendar key that isDateOverdue expects.
   * Xero's /Date(ms)/ is UTC midnight of the due date, so rendering it back in Adelaide
   * gives the calendar date the owner actually reads off the bill.
   */
  const toDueDateKey = (dateStr: string | undefined): string | null => {
    if (!dateStr) return null;
    // Already a calendar date ('2026-08-03', '2026-08-03T00:00:00Z') — slice rather than
    // round-trip through Date, which would re-interpret it as a UTC instant.
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
    const match = dateStr.match(/Date\((\d+)(?:[+-]\d+)?\)/);
    const parsed = match ? new Date(Number(match[1])) : new Date(dateStr);
    if (isNaN(parsed.getTime())) return null;
    // 'en-CA' formats as YYYY-MM-DD natively.
    return parsed.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TZ });
  };

  // Due dates are calendar dates, not instants. Comparing them to `new Date()` badged
  // anything due TODAY as "Overdue" from ~09:30 Adelaide (UTC midnight) onward — a daily
  // false alarm on the accounts-payable run. Compare Adelaide calendar dates instead.
  const isOverdue = () => {
    if (invoice.Status === 'PAID' || invoice.Status === 'VOIDED') return false;
    return isDateOverdue(toDueDateKey(invoice.DueDate || invoice.DueDateString));
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PAID': return 'bg-success';
      // badge-light-warning instead of `bg-warning text-dark`: the dark themes remap
      // .text-dark to a light colour, leaving near-white text on amber.
      case 'AUTHORISED': return 'badge-light-warning';
      case 'SUBMITTED': return 'bg-info';
      case 'DRAFT': return 'bg-secondary';
      case 'VOIDED': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  // Color based on view type — the token map already carries bill_due, so bills can never
  // fall through to the cyan invoice palette.
  const isBill = viewType === 'bill_due';
  const typeColor = CALENDAR_ITEM_COLORS[viewType];
  const typeIcon = viewType === 'invoice_created' ? 'bi-file-earmark-plus' : 'bi-receipt';
  const typeLabel = TYPE_LABELS[viewType];

  return (
    <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          {/* Header */}
          <div className="modal-header border-bottom">
            <div className="d-flex align-items-center gap-3">
              <div 
                className="d-flex align-items-center justify-content-center"
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  background: typeColor,
                  borderRadius: '10px',
                  color: 'white'
                }}
              >
                <i className={`bi ${typeIcon} fs-5`}></i>
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="modal-title mb-0 fw-bold">
                    {invoice.InvoiceNumber}
                  </h5>
                  <span 
                    className="badge"
                    style={{ 
                      fontSize: '0.7rem',
                      background: withAlpha(typeColor, 0.125),
                      color: typeColor,
                      border: `1px solid ${withAlpha(typeColor, 0.25)}`
                    }}
                  >
                    {typeLabel}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span className={`badge ${getStatusBadgeClass(invoice.Status)}`} style={{ fontSize: '0.75rem' }}>
                    {invoice.Status}
                  </span>
                  {isOverdue() && (
                    <span className="badge bg-danger" style={{ fontSize: '0.75rem' }}>
                      Overdue
                    </span>
                  )}
                  <span style={{ fontSize: '0.875rem', color: 'var(--bs-secondary-color)' }}>
                    {invoice.Contact?.Name || 'Unknown Customer'}
                  </span>
                  <span style={{ color: 'var(--bs-secondary-color)' }}>•</span>
                  <span className="fw-semibold" style={{ color: typeColor, fontSize: '0.875rem' }}>
                    {formatCurrency(invoice.AmountDue > 0 ? invoice.AmountDue : invoice.Total)}
                  </span>
                </div>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Info Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div 
                  className="h-100 p-3 rounded-3 text-center"
                  style={{ 
                    background: withAlpha(typeColor, 0.08),
                    border: `1px solid ${withAlpha(typeColor, 0.19)}`
                  }}
                >
                  <i className="bi bi-calendar-event fs-3 d-block mb-2" style={{ color: typeColor }}></i>
                  <small className="d-block" style={{ color: 'var(--bs-secondary-color)' }}>{isBill ? 'Bill Date' : 'Invoice Date'}</small>
                  <span className="fw-bold" style={{ color: typeColor }}>
                    {parseXeroDate(invoice.Date || invoice.DateString)}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div 
                  className="h-100 p-3 rounded-3 text-center"
                  style={{ 
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)'
                  }}
                >
                  <i className={`bi ${isOverdue() ? 'bi-exclamation-triangle' : 'bi-calendar-check'} fs-3 d-block mb-2`} 
                     style={{ color: isOverdue() ? 'var(--bs-danger)' : 'var(--bs-primary)' }}></i>
                  <small className="d-block" style={{ color: 'var(--bs-secondary-color)' }}>Due Date</small>
                  <span className="fw-bold" style={{ color: isOverdue() ? 'var(--bs-danger)' : 'var(--bs-body-color)' }}>
                    {parseXeroDate(invoice.DueDate || invoice.DueDateString)}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div 
                  className="h-100 p-3 rounded-3 text-center"
                  style={{ 
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)'
                  }}
                >
                  <i className="bi bi-currency-dollar fs-3 d-block mb-2" style={{ color: 'var(--bs-primary)' }}></i>
                  <small className="d-block" style={{ color: 'var(--bs-secondary-color)' }}>Total</small>
                  <span className="fw-bold" style={{ color: 'var(--bs-body-color)' }}>
                    {formatCurrency(invoice.Total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="d-flex gap-3 mb-4">
              <div 
                className="flex-fill p-3 rounded-3"
                style={{ 
                  background: 'var(--bs-tertiary-bg)',
                  border: '1px solid var(--bs-border-color)'
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className="bi bi-check-circle" style={{ color: 'var(--bs-success)' }}></i>
                  <small style={{ color: 'var(--bs-secondary-color)' }}>Amount Paid</small>
                </div>
                <span className="fw-semibold" style={{ color: 'var(--bs-success)' }}>
                  {formatCurrency(invoice.AmountPaid)}
                </span>
              </div>
              <div 
                className="flex-fill p-3 rounded-3"
                style={{ 
                  background: 'var(--bs-tertiary-bg)',
                  border: '1px solid var(--bs-border-color)'
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className="bi bi-hourglass-split" style={{ color: isOverdue() ? 'var(--bs-danger)' : 'var(--bs-warning)' }}></i>
                  <small style={{ color: 'var(--bs-secondary-color)' }}>Amount Due</small>
                </div>
                <span className="fw-semibold" style={{ color: isOverdue() ? 'var(--bs-danger)' : 'var(--bs-body-color)' }}>
                  {formatCurrency(invoice.AmountDue)}
                </span>
              </div>
              {invoice.Reference && (
                <div 
                  className="flex-fill p-3 rounded-3"
                  style={{ 
                    background: 'var(--bs-tertiary-bg)',
                    border: '1px solid var(--bs-border-color)'
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <i className="bi bi-tag" style={{ color: 'var(--bs-secondary-color)' }}></i>
                    <small style={{ color: 'var(--bs-secondary-color)' }}>Reference</small>
                  </div>
                  <span className="fw-semibold" style={{ color: 'var(--bs-body-color)' }}>
                    {invoice.Reference}
                  </span>
                </div>
              )}
            </div>

            {/* Line Items */}
            {invoice.LineItems && invoice.LineItems.length > 0 && (
              <div>
                <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: 'var(--bs-body-color)' }}>
                  <i className="bi bi-list-ul" style={{ color: 'var(--bs-secondary-color)' }}></i>
                  Line Items
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
                        <th className="border-0 fw-semibold">Description</th>
                        <th className="border-0 fw-semibold text-center" style={{ width: '80px' }}>Qty</th>
                        <th className="border-0 fw-semibold text-end" style={{ width: '100px' }}>Unit Price</th>
                        <th className="border-0 fw-semibold text-end" style={{ width: '100px' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.LineItems.map((item, index) => (
                        <tr key={index} style={{ color: 'var(--bs-body-color)' }}>
                          <td className="border-0">{item.Description || '-'}</td>
                          <td className="border-0 text-center">{item.Quantity}</td>
                          <td className="border-0 text-end">{formatCurrency(item.UnitAmount)}</td>
                          <td className="border-0 text-end fw-semibold">{formatCurrency(item.LineAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ color: 'var(--bs-body-color)' }}>
                        <td colSpan={3} className="text-end fw-bold border-0 pt-3">Total:</td>
                        <td className="text-end fw-bold border-0 pt-3" style={{ color: typeColor }}>
                          {formatCurrency(invoice.Total)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 pt-0">
            <button 
              type="button" 
              className="btn"
              style={{
                background: 'var(--bs-secondary-bg)',
                border: '1px solid var(--bs-border-color)',
                color: 'var(--bs-body-color)'
              }}
              onClick={onClose}
            >
              Close
            </button>
            {/* Router navigation, not an <a href> — the anchor full-page-reloaded the SPA.
                /invoices and /bills are <Navigate> redirects that drop query params, so
                point straight at the real accounting surface. */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                onClose();
                navigate(isBill ? '/accounting?view=bills' : '/accounting?view=invoices');
              }}
            >
              <i className="bi bi-arrow-right me-1"></i>
              {isBill ? 'View All Bills' : 'View All Invoices'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceQuickViewModal;
